import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildGetDDFetchKey,
  buildGetDDFieldsFromParams,
  extractPrimitiveFieldValue,
  fetchAgentGetDDOptions,
  parseGetDDRowsToOptions,
} from "./agentGetDD";

const isDynamicField = (field) =>
  Boolean(field?.schema?.dynamic || (field?.schema?.perform && !field?.schema?.type));

function axiosIsAbort(err) {
  return (
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    err?.code === "ERR_CANCELED"
  );
}

/**
 * Load dynamic dropdown options for agent trigger/action field modals.
 * Debounces field edits, aborts stale getDD calls, and skips unchanged fetch keys
 * (same pattern as workflow FieldSetupWidget).
 */
export function useAgentDynamicGetDD({
  open,
  appMeta,
  credentialId,
  localFields,
  parseDynamicSpec,
  parsePerformSpec,
}) {
  const [dynamicOptionsByField, setDynamicOptionsByField] = useState({});
  const [dynamicLoadingByField, setDynamicLoadingByField] = useState({});
  const [loadingDynamic, setLoadingDynamic] = useState(false);
  const lastFetchKeysRef = useRef(new Map());

  const fieldValueSignature = useMemo(
    () =>
      JSON.stringify(
        (localFields || []).map((field) => ({
          name: field?.name,
          value: extractPrimitiveFieldValue(field?.value),
        })),
      ),
    [localFields],
  );

  const [debouncedSignature, setDebouncedSignature] = useState(fieldValueSignature);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSignature(fieldValueSignature), 400);
    return () => clearTimeout(timer);
  }, [fieldValueSignature]);

  useEffect(() => {
    if (!open) {
      lastFetchKeysRef.current = new Map();
      return undefined;
    }

    const workingFields = Array.isArray(localFields) ? JSON.parse(JSON.stringify(localFields)) : [];
    const dynamicTargets = workingFields.filter(isDynamicField);
    if (dynamicTargets.length === 0 || !appMeta?.url || !credentialId) {
      setLoadingDynamic(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    const run = async () => {
      setLoadingDynamic(true);

      for (const field of dynamicTargets) {
        const fieldName = field?.name;
        if (!fieldName) continue;

        const dynamicParsed = field?.schema?.dynamic ? parseDynamicSpec(field.schema.dynamic) : null;
        const performParsed = field?.schema?.perform ? parsePerformSpec(field.schema.perform) : null;
        const parsed = dynamicParsed || performParsed;
        if (!parsed) continue;

        const fetchKey = buildGetDDFetchKey(
          field,
          credentialId,
          appMeta.url,
          appMeta.app,
          workingFields,
        );
        if (fetchKey === lastFetchKeysRef.current.get(fieldName)) {
          continue;
        }

        if (!cancelled) {
          setDynamicLoadingByField((prev) => ({ ...prev, [fieldName]: true }));
        }

        try {
          const payloadFields = buildGetDDFieldsFromParams(field?.schema?.params, workingFields);
          const responseData = await fetchAgentGetDDOptions(
            {
              url: appMeta.url,
              app: parsed.app || appMeta.app,
              credential: credentialId,
              action: parsed.action,
              fields: payloadFields,
            },
            { signal: controller.signal },
          );
          if (cancelled) return;
          const options = parseGetDDRowsToOptions(responseData, parsed);
          setDynamicOptionsByField((prev) => ({ ...prev, [fieldName]: options }));
          lastFetchKeysRef.current.set(fieldName, fetchKey);
        } catch (err) {
          if (axiosIsAbort(err)) return;
          setDynamicOptionsByField((prev) => ({ ...prev, [fieldName]: [] }));
          lastFetchKeysRef.current.set(fieldName, fetchKey);
        } finally {
          if (!cancelled) {
            setDynamicLoadingByField((prev) => ({ ...prev, [fieldName]: false }));
          }
        }
      }

      if (!cancelled) setLoadingDynamic(false);
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debouncedSignature gates refetch
  }, [open, appMeta?.url, appMeta?.app, credentialId, debouncedSignature]);

  return { dynamicOptionsByField, dynamicLoadingByField, loadingDynamic };
}
